import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Tooltip, alpha, useTheme } from '@mui/material';
import {
  AccessTime as ClockIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

/**
 * TimeTracker Component
 * Displays time tracking for time-based tasks
 * Supports both deadline and duration-based tasks
 */
const TimeTracker = ({ task, showProgress = true }) => {
  const theme = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute for live countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  if (!task.is_time_based) {
    return (
      <Typography variant="caption" color="text.secondary">
        No deadline
      </Typography>
    );
  }

  const calculateTimeInfo = () => {
    const now = new Date();

    // Deadline-based task
    if (task.time_type === 'deadline' && task.deadline) {
      const deadline = new Date(task.deadline);
      const diff = deadline - now;
      const totalSeconds = Math.floor(diff / 1000);

      const isOverdue = totalSeconds < 0;
      const absSeconds = Math.abs(totalSeconds);

      const hours = Math.floor(absSeconds / 3600);
      const days = Math.floor(hours / 24);

      let message, displayValue, displayUnit, color, severity;

      if (isOverdue) {
        if (hours < 24) {
          message = `${hours}h overdue`;
          displayValue = hours;
          displayUnit = 'hours';
        } else {
          message = `${days}d overdue`;
          displayValue = days;
          displayUnit = 'days';
        }
        color = theme.palette.error.main;
        severity = 'error';
      } else {
        // Approaching deadline warnings
        if (hours < 1) {
          const minutes = Math.floor(absSeconds / 60);
          message = `${minutes}m remaining`;
          displayValue = minutes;
          displayUnit = 'minutes';
          color = theme.palette.error.main;
          severity = 'error';
        } else if (hours < 24) {
          message = `${hours}h remaining`;
          displayValue = hours;
          displayUnit = 'hours';
          color = hours < 3 ? theme.palette.warning.main : theme.palette.info.main;
          severity = hours < 3 ? 'warning' : 'info';
        } else {
          message = `${days}d remaining`;
          displayValue = days;
          displayUnit = 'days';
          color = theme.palette.success.main;
          severity = 'success';
        }
      }

      return { message, displayValue, displayUnit, color, severity, isOverdue };
    }

    // Duration-based task
    if (task.time_type === 'duration' && task.started_at) {
      const startedAt = new Date(task.started_at);
      const elapsed = now - startedAt;
      const elapsedSeconds = Math.floor(elapsed / 1000);

      const totalSeconds =
        task.duration_unit === 'hours' ? task.duration_value * 3600 : task.duration_value * 86400;

      const remainingSeconds = totalSeconds - elapsedSeconds;
      const percentage = (elapsedSeconds / totalSeconds) * 100;

      const isOverdue = remainingSeconds < 0;
      const absRemaining = Math.abs(remainingSeconds);

      const elapsedHours = Math.floor(elapsedSeconds / 3600);
      const elapsedDays = Math.floor(elapsedHours / 24);

      let message, color, severity;

      if (isOverdue) {
        const overdueHours = Math.floor(absRemaining / 3600);
        const overdueDays = Math.floor(overdueHours / 24);

        if (task.duration_unit === 'hours') {
          message = `${overdueHours}h overdue`;
        } else {
          message = `${overdueDays}d overdue`;
        }
        color = theme.palette.error.main;
        severity = 'error';
      } else {
        const remainingHours = Math.floor(absRemaining / 3600);
        const remainingDays = Math.floor(remainingHours / 24);

        if (task.duration_unit === 'hours') {
          message = `${elapsedHours}h / ${task.duration_value}h`;
        } else {
          message = `${elapsedDays}d / ${task.duration_value}d`;
        }

        // Color based on percentage
        if (percentage > 90) {
          color = theme.palette.error.main;
          severity = 'error';
        } else if (percentage > 75) {
          color = theme.palette.warning.main;
          severity = 'warning';
        } else {
          color = theme.palette.info.main;
          severity = 'info';
        }
      }

      return {
        message,
        color,
        severity,
        isOverdue,
        percentage: Math.min(percentage, 100),
        showProgress: true,
      };
    }

    // Duration task not started yet
    if (task.time_type === 'duration' && !task.started_at) {
      return {
        message: `${task.duration_value} ${task.duration_unit} allocated`,
        color: theme.palette.text.secondary,
        severity: 'info',
        isOverdue: false,
        showProgress: false,
      };
    }

    return null;
  };

  const timeInfo = calculateTimeInfo();

  if (!timeInfo) {
    return null;
  }

  const Icon = timeInfo.isOverdue
    ? ErrorIcon
    : timeInfo.severity === 'warning'
      ? WarningIcon
      : ClockIcon;

  return (
    <Box>
      <Tooltip title={`${task.time_type === 'deadline' ? 'Deadline' : 'Duration'} based task`}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Icon sx={{ fontSize: 16, color: timeInfo.color }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: timeInfo.color,
            }}
          >
            {timeInfo.message}
          </Typography>
        </Box>
      </Tooltip>

      {showProgress && timeInfo.showProgress && (
        <LinearProgress
          variant="determinate"
          value={timeInfo.percentage}
          sx={{
            mt: 0.5,
            height: 4,
            borderRadius: 2,
            backgroundColor: alpha(timeInfo.color, 0.1),
            '& .MuiLinearProgress-bar': {
              backgroundColor: timeInfo.color,
            },
          }}
        />
      )}
    </Box>
  );
};

export default TimeTracker;
